import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../../lib/api.js";
import { Plus, Trash2 } from "lucide-react";
import { inputCls } from "./_shared.jsx";

export function DwellThresholdsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ state: "", max_hours: 24 });

  const SOM_STATES_WITH_DWELL = [
    "Obra aberta", "Recebido", "Na fila da oficina", "Em diagnóstico",
    "Aguarda classificação", "Orçamento em preparação", "Orçamento pronto",
    "Aguarda envio ao cliente", "Aguarda aprovação do cliente", "Aprovado",
    "Aguarda pagamento ou PO", "Aguarda pedido de peças", "Aguarda peças",
    "Peças disponíveis", "Entrada marcada", "Em reparação",
    "Controlo de qualidade", "Em lavagem", "Pronto para entrega",
    "Entregue", "Chamada de cortesia",
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/dwell-thresholds");
      setRows(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao carregar.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const usedStates = new Set(rows.map((r) => r.state));
  const availableStates = SOM_STATES_WITH_DWELL.filter((s) => !usedStates.has(s));

  const handleCreate = async () => {
    if (!draft.state || !draft.max_hours) return;
    try {
      await api.post("/admin/dwell-thresholds", { entity: "som", state: draft.state, max_hours: Number(draft.max_hours), active: true });
      setDraft({ state: "", max_hours: 24 });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao criar.");
    }
  };

  const handleSave = async (id, next) => {
    try {
      await api.patch(`/admin/dwell-thresholds/${id}`, next);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao guardar.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este limite?")) return;
    try {
      await api.delete(`/admin/dwell-thresholds/${id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao eliminar.");
    }
  };

  if (loading) return <div className="text-sm text-[#6C757D]" data-testid="dwell-loading">A carregar limites…</div>;

  return (
    <div data-testid="dwell-thresholds-tab">
      <div className="mb-3 text-sm text-[#6C757D]">
        Quando uma obra fica num estado por mais tempo do que o limite definido, o sistema cria automaticamente uma task &ldquo;Verificar bloqueio em &lt;estado&gt;&rdquo; atribuída ao service advisor responsável. Prioridade aumenta com o tempo decorrido (Normal → High → Crítica).
      </div>

      {error && <div className="mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="dwell-error">{error}</div>}

      <table className="w-full text-sm bg-white border border-gray-200 rounded-md overflow-hidden">
        <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
          <tr>
            <th className="text-left px-3 py-2">Estado SOM</th>
            <th className="text-right px-3 py-2">Limite (horas)</th>
            <th className="text-center px-3 py-2">Activo</th>
            <th className="text-right px-3 py-2 w-32">Acções</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const editing = editingId === r.id;
            return (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`dwell-row-${r.state}`}>
                <td className="px-3 py-2">{r.state}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {editing ? (
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      defaultValue={r.max_hours}
                      onChange={(e) => (r._draft = Number(e.target.value))}
                      className="border border-gray-300 rounded-sm px-2 py-0.5 w-20 text-right"
                      data-testid={`dwell-input-hours-${r.state}`}
                    />
                  ) : `${r.max_hours} h`}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => handleSave(r.id, { active: !r.active })}
                    className={`text-xs px-2 py-0.5 rounded-sm ${r.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}
                    data-testid={`dwell-toggle-${r.state}`}
                  >
                    {r.active ? "ON" : "OFF"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  {editing ? (
                    <>
                      <button
                        onClick={() => handleSave(r.id, { max_hours: r._draft ?? r.max_hours })}
                        className="text-xs px-2 py-1 bg-[#1F3864] text-white rounded-sm mr-1"
                        data-testid={`dwell-save-${r.state}`}
                      >Guardar</button>
                      <button onClick={() => setEditingId(null)} className="text-xs px-2 py-1 border border-gray-300 rounded-sm">Cancelar</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditingId(r.id)} className="text-xs text-[#1F3864] hover:underline mr-3" data-testid={`dwell-edit-${r.state}`}>Editar</button>
                      <button onClick={() => handleDelete(r.id)} className="text-xs text-[#DC3545] hover:underline" data-testid={`dwell-delete-${r.state}`}>Eliminar</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {availableStates.length > 0 && (
            <tr className="border-t border-gray-100 bg-[#F4F6F8]/50">
              <td className="px-3 py-2">
                <select
                  value={draft.state}
                  onChange={(e) => setDraft({ ...draft, state: e.target.value })}
                  className="border border-gray-300 rounded-sm px-2 py-1 w-full"
                  data-testid="dwell-new-state"
                >
                  <option value="">— escolher estado —</option>
                  {availableStates.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-3 py-2 text-right">
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={draft.max_hours}
                  onChange={(e) => setDraft({ ...draft, max_hours: e.target.value })}
                  className="border border-gray-300 rounded-sm px-2 py-1 w-24 text-right font-mono"
                  data-testid="dwell-new-hours"
                />
              </td>
              <td></td>
              <td className="px-3 py-2 text-right">
                <button
                  onClick={handleCreate}
                  disabled={!draft.state}
                  className="text-xs px-3 py-1 bg-[#1F3864] text-white rounded-sm disabled:opacity-50"
                  data-testid="dwell-create-btn"
                >Adicionar</button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-[#94A3B8]">{rows.length} limite(s) configurado(s) · varredura corre a cada 5 minutos no loop de fundo</div>
    </div>
  );
}

// ===== SLA per brand management =====
